import React, { useMemo, useRef, useState } from "react";
import { Bot, ChevronDown, ExternalLink, Maximize2, MessageCircle, Minimize2, Send, Sparkles, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../utils/api";
import { getUserPrivileges, getUserRole, isAuthenticated } from "../../utils/auth";
import crmAssistantKnowledge, { suggestedAssistantQuestions } from "../../config/crmAssistantKnowledge";
import "./AIAssistant.css";
import { API_BASE_URL } from '../../utils/apiBase';

const hasAccess = (item, role, privileges) => {
  if (role === "admin") return true;
  if (item.adminOnly) return false;
  if (!item.privilege && !(item.alternatePrivileges || []).length) return true;
  return [item.privilege, ...(item.alternatePrivileges || [])].filter(Boolean).some((key) => privileges.includes(key));
};

const findCurrentPage = (pathname) => crmAssistantKnowledge.find((item) => item.path === pathname);

const createFallbackReply = (message, visibleKnowledge, currentPage) => {
  const normalized = message.toLowerCase();
  const scored = visibleKnowledge
    .map((item) => {
      const haystack = [
        item.title,
        item.module,
        item.description,
        ...(item.keywords || []),
        ...(item.actions || []),
        ...(item.menuPath || [])
      ].join(" ").toLowerCase();
      const score = normalized
        .split(/\s+/)
        .filter((word) => word.length > 2 && haystack.includes(word)).length;
      return { ...item, score };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3);

  const target = scored[0] || currentPage;

  if (!target) {
    return {
      answer: "I can help with policies, endorsements, claims, renewals, reports, invoices, masters, users, and roles. Try asking where to find a module or how to complete a CRM workflow.",
      actions: []
    };
  }

  return {
    answer: [
      `${target.title} is available from ${target.menuPath.join(" > ")}.`,
      target.description,
      target.actions?.length ? `There you can: ${target.actions.join(", ")}.` : "",
      currentPage ? `You are currently on ${currentPage.title}, so I can also explain fields and actions on this page.` : ""
    ].filter(Boolean).join("\n\n"),
    actions: scored.map((item) => ({
      label: `Open ${item.title}`,
      path: item.path,
      menuPath: item.menuPath
    }))
  };
};

const AIAssistant = () => {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hi, I am your CRM assistant. Ask me where to find something, how a workflow works, or what to do next on this page.",
      actions: []
    }
  ]);
  const navigate = useNavigate();
  const location = useLocation();
  const endRef = useRef(null);

  const role = getUserRole();
  const privileges = getUserPrivileges();
  const currentPage = findCurrentPage(location.pathname);

  const visibleKnowledge = useMemo(
    () => crmAssistantKnowledge.filter((item) => hasAccess(item, role, privileges)),
    [role, privileges]
  );

  if (!isAuthenticated() || ["/", "/login", "/forgot-password"].includes(location.pathname)) {
    return null;
  }

  const scrollToBottom = () => {
    window.requestAnimationFrame(() => endRef.current?.scrollIntoView({ behavior: "smooth" }));
  };

  const sendMessage = async (text = input) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;

    const userMessage = { role: "user", content: trimmed };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);
    scrollToBottom();

    try {
      const { data } = await api.post(`${API_BASE_URL}/api/assistant/chat`, {
        message: trimmed,
        currentPath: location.pathname,
        currentPage,
        knowledge: visibleKnowledge
      });

      const assistantMessage = {
        role: "assistant",
        content: data.answer,
        actions: data.actions || []
      };
      setMessages((prev) => [...prev, assistantMessage]);

      if (data.autoNavigate && data.actions?.[0]?.path) {
        navigate(data.actions[0].path);
      }
    } catch (error) {
      const fallback = createFallbackReply(trimmed, visibleKnowledge, currentPage);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: fallback.answer,
          actions: fallback.actions,
          muted: true
        }
      ]);
    } finally {
      setLoading(false);
      scrollToBottom();
    }
  };

  const handleNavigate = (path) => {
    navigate(path);
    setMinimized(true);
  };

  return (
    <div className={`ai-assistant ${open ? "is-open" : ""} ${minimized ? "is-minimized" : ""}`}>
      {open && !minimized && (
        <section className="ai-assistant-panel" aria-label="CRM AI Assistant">
          <header className="ai-assistant-header">
            <div className="ai-assistant-title">
              <span className="ai-assistant-avatar"><Sparkles size={18} /></span>
              <div>
                <strong>CRM Assistant</strong>
                <small>{currentPage ? `Context: ${currentPage.title}` : "Navigation and workflow help"}</small>
              </div>
            </div>
            <div className="ai-assistant-controls">
              <button type="button" onClick={() => setMinimized(true)} aria-label="Minimize assistant"><Minimize2 size={16} /></button>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close assistant"><X size={16} /></button>
            </div>
          </header>

          <div className="ai-assistant-suggestions">
            {suggestedAssistantQuestions.slice(0, 4).map((question) => (
              <button type="button" key={question} onClick={() => sendMessage(question)}>
                {question}
              </button>
            ))}
          </div>

          <div className="ai-assistant-messages">
            {messages.map((message, index) => (
              <div className={`ai-message ${message.role === "user" ? "from-user" : "from-assistant"}`} key={`${message.role}-${index}`}>
                <div className="ai-message-bubble">
                  {message.role === "assistant" && <Bot size={16} />}
                  <p>{message.content}</p>
                </div>
                {!!message.actions?.length && (
                  <div className="ai-actions">
                    {message.actions.slice(0, 3).map((action) => (
                      <button type="button" key={`${action.path}-${action.label}`} onClick={() => handleNavigate(action.path)}>
                        <ExternalLink size={14} />
                        {action.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="ai-message from-assistant">
                <div className="ai-typing"><span></span><span></span><span></span></div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <form className="ai-assistant-input" onSubmit={(event) => { event.preventDefault(); sendMessage(); }}>
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="Ask about policies, claims, reports..."
            />
            <button type="submit" disabled={loading || !input.trim()} aria-label="Send message"><Send size={18} /></button>
          </form>
        </section>
      )}

      <button
        type="button"
        className="ai-assistant-launcher"
        onClick={() => {
          setOpen(true);
          setMinimized((value) => (open ? !value : false));
        }}
        aria-label={open && minimized ? "Expand CRM assistant" : "Open CRM assistant"}
      >
        {open && minimized ? <Maximize2 size={22} /> : open ? <ChevronDown size={22} /> : <MessageCircle size={24} />}
      </button>
    </div>
  );
};

export default AIAssistant;
